/**
 * The field between plays (slice 8b): both charts standing in formation, so the field is never
 * blank. Where everyone stands comes from the sim (`snapFormation`, the same placement the snap
 * uses), with the personnel the draft previews. The player's side lines up in the drafted play;
 * the AI's side in its base play (its playbook's first), never its real call: on defense the
 * player would read the AI's play off it, and on offense the AI has not called yet.
 */
import {
  DEFAULT_PLAYBOOK,
  lineupOf,
  playById,
  snapFormation,
  type Game,
  type GameState,
  type Playbook,
  type PlayEvent,
  type TeamId,
} from '@tdmons/sim';
import { unitsOf, type FieldView } from '../field/playView';
import { preview, type Draft } from './draft';

/** The state the next snap starts from, with the draft's window applied. */
export function preSnapState(game: Readonly<Game>, d: Draft): GameState {
  const g = preview(game, d);
  const o = g.possession;
  const def: TeamId = o === 'A' ? 'B' : 'A';
  return {
    offense: { lineup: lineupOf(g.teams[o], g.teams[o].offense) },
    defense: { lineup: lineupOf(g.teams[def], g.teams[def].defense) },
    ballOn: g.ballOn,
    down: g.down,
    toGo: g.toGo,
  };
}

/** The formation as a field view: its log is the snap event alone. */
export function formationView(
  game: Readonly<Game>,
  d: Draft,
  book: Playbook = DEFAULT_PLAYBOOK,
): FieldView {
  const before = preSnapState(game, d);
  const offense = playById(
    book.offense,
    d.side === 'offense' ? (d.playId ?? undefined) : undefined,
  );
  const defense = playById(
    book.defense,
    d.side === 'defense' ? (d.playId ?? undefined) : undefined,
  );
  const snap: PlayEvent = { t: 0, type: 'snap', ...snapFormation(before, { offense, defense }) };
  return {
    title: `${offense.name} vs ${defense.name}`,
    before,
    events: [snap],
    units: unitsOf(before),
  };
}
